import {ChangeDetectorRef} from '@angular/core';
import {ModalComponent} from '../modal.component';

export abstract class ScrollableModalListBase<T> {
  protected readonly pageRows = 20;
  protected readonly maxRenderedItems = 100;
  protected readonly loadMoreThreshold = 120;
  protected readonly fallbackSkeletonRowHeight = 116;
  protected readonly skeletonOverscanRows = 1;
  protected heightTimeoutId: ReturnType<typeof setTimeout> | null = null;
  protected skeletonMeasureTimeoutId: ReturnType<typeof setTimeout> | null = null;
  protected resizeObserver: ResizeObserver | null = null;

  public readonly skeletonThumbs = Array.from({length: 5});
  public skeletonRows = Array.from({length: 6});
  public initialLoading = true;
  public loadingMore = false;
  public hasMore = false;
  public scrollerHeight = '60vh';
  public unloadedItemsCount = 0;

  protected abstract readonly cdr: ChangeDetectorRef;
  protected abstract readonly modalComponent: ModalComponent;
  protected abstract get listContainerElement(): HTMLElement | null;
  protected abstract loadMoreItems(): void;
  protected abstract mergeItems(existingItems: T[], incomingItems: T[]): T[];

  protected initScrollableList(): void {
    this.scheduleHeightUpdate();
    this.initResizeObserver();
  }

  protected destroyScrollableList(): void {
    if (this.heightTimeoutId) {
      clearTimeout(this.heightTimeoutId);
    }
    if (this.skeletonMeasureTimeoutId) {
      clearTimeout(this.skeletonMeasureTimeoutId);
    }
    this.resizeObserver?.disconnect();
  }

  protected handleResize(): void {
    this.scheduleHeightUpdate();
  }

  public onScroll(event: Event): void {
    if (!this.hasMore || this.loadingMore) {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceToBottom > this.loadMoreThreshold) {
      return;
    }

    this.loadMoreItems();
  }

  protected appendRenderedItems(currentItems: T[], incomingItems: T[]): T[] {
    if (!incomingItems.length) {
      return currentItems;
    }

    const merged = this.mergeItems(currentItems, incomingItems);
    const overflow = Math.max(0, merged.length - this.maxRenderedItems);
    if (overflow > 0) {
      this.unloadedItemsCount += overflow;
      return merged.slice(overflow);
    }

    return merged;
  }

  protected tryLoadNextIfNotScrollable(): void {
    if (!this.hasMore || this.loadingMore) {
      return;
    }

    setTimeout(() => {
      const container = this.listContainerElement;
      if (!container || container.scrollHeight > container.clientHeight + 1) {
        return;
      }
      this.loadMoreItems();
    });
  }

  protected scheduleHeightUpdate(): void {
    if (this.heightTimeoutId) {
      clearTimeout(this.heightTimeoutId);
    }

    this.heightTimeoutId = setTimeout(() => {
      const dialogHeight = this.modalComponent.dialogHeight();
      const contentEl = this.listContainerElement?.closest('.p-dialog-content') as HTMLElement | null;
      let baseHeight = contentEl?.clientHeight || (dialogHeight ? dialogHeight - 150 : 0);

      if (contentEl && typeof window !== 'undefined') {
        const styles = window.getComputedStyle(contentEl);
        const paddingTop = Number.parseFloat(styles.paddingTop || '0') || 0;
        const paddingBottom = Number.parseFloat(styles.paddingBottom || '0') || 0;
        baseHeight -= paddingTop + paddingBottom;
      }

      if (!baseHeight) {
        return;
      }

      const nextHeight = `${Math.max(baseHeight, 240)}px`;
      if (nextHeight !== this.scrollerHeight) {
        this.scrollerHeight = nextHeight;
      }

      this.updateSkeletonRows(Math.max(baseHeight, 240));
      this.cdr.detectChanges();
      this.scheduleSkeletonMeasurement();
    }, 0);
  }

  protected updateSkeletonRows(availableHeight?: number): void {
    const fallbackHeight = typeof window !== 'undefined' ? Math.max(window.innerHeight * 0.6, 240) : 480;
    const targetHeight = availableHeight || this.listContainerElement?.clientHeight || fallbackHeight;
    const rowCount = Math.max(3, Math.ceil(targetHeight / this.fallbackSkeletonRowHeight) + this.skeletonOverscanRows);
    this.skeletonRows = Array.from({length: rowCount});
  }

  protected scheduleSkeletonMeasurement(): void {
    if (!this.initialLoading) {
      return;
    }
    if (this.skeletonMeasureTimeoutId) {
      clearTimeout(this.skeletonMeasureTimeoutId);
    }
    this.skeletonMeasureTimeoutId = setTimeout(() => this.measureAndSyncSkeletonRows(), 0);
  }

  protected initResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.scheduleHeightUpdate());

    setTimeout(() => {
      const contentEl = this.listContainerElement?.closest('.p-dialog-content') as HTMLElement | null;
      if (contentEl) {
        this.resizeObserver?.observe(contentEl);
      }
    });
  }

  protected measureAndSyncSkeletonRows(): void {
    if (!this.initialLoading) {
      return;
    }

    const container = this.listContainerElement;
    if (!container || !this.skeletonRows.length) {
      return;
    }

    const totalRenderedHeight = container.scrollHeight;
    if (!totalRenderedHeight) {
      return;
    }

    const rowHeight = totalRenderedHeight / this.skeletonRows.length;
    const visibleHeight = container.clientHeight || parseFloat(this.scrollerHeight) || 0;
    if (!visibleHeight || !rowHeight) {
      return;
    }

    const rowCount = Math.max(3, Math.ceil(visibleHeight / rowHeight) + this.skeletonOverscanRows);
    if (rowCount === this.skeletonRows.length) {
      return;
    }

    this.skeletonRows = Array.from({length: rowCount});
    this.cdr.detectChanges();
    this.scheduleSkeletonMeasurement();
  }
}
